import StateObserver, { now, user } from 'src/StateObserver';
import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import * as match2TournamentGetters from 'src/replicant/match2/tournament/match2-tournament.getters';
import { withLoading } from 'src/lib/utils';
import { getTournament } from './match2-tournament.utils';
import { openMatch2RewardsPopup } from '../rewards/match2-rewards.utils';
import { Feature } from 'src/replicant/analytics';
import { playMatch2 } from '../match2.play';
import { track } from 'src/lib/analytics/utils';

type OriginFeature = 'hud' | 'launch' | 'postPlay';

/**
 * Initialize the match2 tournament model (if feature is available), fetching user and tournament data
 */
export function initMatch2Tournament() {
  const user = StateObserver.getState().user;

  // Skip if the feature is not enabled
  if (!match2TournamentGetters.isTournamentFeatureEnabled(user)) return;

  // Fetch the current tournament event. Don't wait for it to finish.
  void getTournament()?.get();

  // Check if there are events with unclaimed rewards
  let unclaimedEvents = match2TournamentGetters.getUnclaimedEvents(user);

  if (!unclaimedEvents.length) {
    // If there are no unclaimed events, check for past events with unclaimed rewards
    unclaimedEvents = match2TournamentGetters.getPastUnclaimedEvents(
      user,
      now(),
    );
  }

  // If there are unclaimed events, fetch the their tournament data too
  if (unclaimedEvents) {
    unclaimedEvents.forEach((event) => {
      void getTournament(event.id)?.get();
    });
  }
}

/**
 * Open the match2 tournament popup, if the feature is enabled for the player and pet is not dead
 */
export async function openMatch2TournamentPopup(
  originFeature: OriginFeature,
): Promise<boolean> {
  await withLoading(() => refreshTournament(), {
    feature: Feature.TOURNAMENT._,
    subFeature: Feature.TOURNAMENT.REFRESH,
  });

  track('TournamentOpen', {
    originFeature,
    type: match2TournamentGetters.getTournamentType(user(), now()),
  });

  // Check if user has any unclaimed tournaments, if so open the popup with the earliest one
  const unclaimed = match2TournamentGetters.getUnclaimedEvents(
    StateObserver.getState().user,
  );

  const origin = mapFeatureToDialogOrigin(originFeature);

  if (unclaimed.length > 0) {
    const unclaimedEvent = getTournament(unclaimed[0].id)?.getCached();
    if (unclaimedEvent) {
      const type = match2TournamentGetters.getStoredTournamentType(
        user(),
        unclaimed[0].id,
      );
      const success = await openPopupPromise(
        type === 'star'
          ? 'popupMatch2Tournament'
          : 'popupMatch2TournamentStreak',
        {
          useClickOutside: true,
          tournamentData: unclaimedEvent,
          origin,
        },
      );

      if (success) {
        const claimed =
          await StateObserver.invoke.claimMatch2TournamentRewards();
        if (claimed) {
          const rank = unclaimedEvent?.finalRank ?? 0;
          const rewards = match2TournamentGetters.getRewardsByRank(rank);
          await openMatch2RewardsPopup({
            rewards,
            useSfx: true,
            origin,
            tracking: {
              feature: Feature.MATCH2.TOURNAMENT._,
              $subFeature: Feature.MATCH2.TOURNAMENT.REWARDS,
            },
          });
        }
      }
    }
  }

  // Get current tournament event being played by the user
  const ongoing = match2TournamentGetters.getOngoingEvent(
    StateObserver.getState().user,
    StateObserver.now(),
  );

  if (ongoing) {
    const ongoingEvent = getTournament(ongoing.id)?.getCached();
    if (ongoingEvent) {
      const type = match2TournamentGetters.getStoredTournamentType(
        user(),
        ongoing.id,
      );
      const success = await openPopupPromise(
        type === 'star'
          ? 'popupMatch2Tournament'
          : 'popupMatch2TournamentStreak',
        {
          useClickOutside: true,
          tournamentData: ongoingEvent,
          origin,
        },
      );
      if (success) playMatch2({ origin });
      return success;
    }
  }

  // No unclaimed or ongoing tournaments, show the info popup instead
  const success = await openPopupPromise('popupMatch2TournamentInfo', {
    origin,
  });
  if (success) playMatch2({ origin });
  return success;
}

/**
 * Called every time a match2 game ends, for post-game tournament logic
 */
export async function match2TournamentPostGame(hasWon: boolean) {
  if (!hasWon) return false;

  const user = StateObserver.getState().user;
  const now = StateObserver.now();

  // Skip if the feature is not enabled or if there are no ongoing tournaments
  if (
    !match2TournamentGetters.canPlay(user, now) ||
    !match2TournamentGetters.getOngoingEvent(user, now)
  )
    return false;

  // Get the rank of the player from before the gameplay - the local cache hasn't been updated yet
  let cachedTournament = getTournament()?.getCached();

  if (cachedTournament) {
    // Deep clone the cached tournament to avoid modifying the cache.
    cachedTournament = JSON.parse(JSON.stringify(cachedTournament));
  }

  // Get the current event id.
  const ongoingEvent = match2TournamentGetters.getOngoingEvent(user, now);
  if (!ongoingEvent) return false;
  const eventId = ongoingEvent.id;

  // Get the tournament event data from the cache.
  const lastRank = cachedTournament?.currentRank;

  // Update the score to get a forecast of the new rank.
  const playerEntry = cachedTournament?.leaderboard.find(
    (entry) => entry.id === user.id,
  );
  if (playerEntry && cachedTournament) {
    // Up to date data.
    const stateTournament = user.match2?.tournament.events[eventId]?.player;
    playerEntry.score = stateTournament?.score || 0;
    playerEntry.scoreUpdatedAt = stateTournament?.scoreUpdatedAt || 0;

    // Sort the leaderboard to get the new rank.
    cachedTournament.leaderboard.sort(
      (a, b) => a.scoreUpdatedAt - b.scoreUpdatedAt,
    );
    cachedTournament.leaderboard.sort((a, b) => b.score - a.score);

    // Update the current rank.
    cachedTournament.currentRank =
      cachedTournament.leaderboard.findIndex((entry) => entry.id === user.id) +
      1;
  }

  // Get the forecasted rank of the player after the gameplay.
  const rankForecast = cachedTournament?.currentRank;

  // Skip if the player is not in the top 3 or if the last rank was in the top 3
  const skipPopup =
    rankForecast && lastRank && (rankForecast > 3 || lastRank <= 3);

  // Skip if matchmaking has happened
  // 'base' is before matchmaking
  const matchmakingRan =
    (cachedTournament?.matchMakingCriteria ?? 'base') !== 'base';

  if (skipPopup && matchmakingRan) return false;

  // Invalidate the tournament cache to fetch the updated data.
  // Perform this action with loading to prevent user interaction during the process
  await withLoading(
    async () => {
      await getTournament(eventId)?.invalidate();
    },
    {
      feature: Feature.TOURNAMENT._,
      subFeature: Feature.TOURNAMENT.INVALIDATE_CACHE,
    },
  );

  // Open the tournament popup to show the player's new rank, and return true if "play" has been selected
  return await openMatch2TournamentPopup('postPlay');
}

export async function refreshTournament() {
  // Cleanup tournaments older than 30 days.
  await StateObserver.invoke.match2CleanupOldTournaments();

  // List of unfinished events without finalRank
  const unfinishedEvents = match2TournamentGetters
    .getUnfinishedEvents(StateObserver.getState().user)
    .filter((event) => !event.finalRank);

  // Get current eventId.
  const currentEvent = match2TournamentGetters.getOngoingEvent(user(), now());

  // End all unfinished events.
  for (const { id } of unfinishedEvents) {
    if (id === currentEvent?.id) continue;
    await StateObserver.invoke.endMatch2TournamentEvent({ eventId: id });
  }

  // No ongoing event, bail out.
  if (!currentEvent) return;

  // Run matchmaking for the current event.
  if (!currentEvent.matchMakingCriteria) {
    await StateObserver.invoke.runMatch2TournamentMatchmaking({
      eventId: currentEvent.id,
    });
  }

  // Fetch the tournament data.
  await getTournament()?.get();
}
